import { PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { NetworkSchemaType } from 'src/global/schemas/network.schema';
import { setNetwork, setNetworkInfo } from 'src/store/actions/app.action';
import { useGetNetworksQuery } from 'src/store/services/network.service';
import { useAppSelector } from 'src/store/utils';

interface NetworkContextType {
  networkList: NetworkSchemaType[];
  selectedNetwork: string | null;
  isNetworkInitialized: boolean;
  updateNetwork: (network: string) => void;
}

const NetworkContext = createContext<NetworkContextType>({
  networkList: [],
  selectedNetwork: null,
  isNetworkInitialized: false,
  updateNetwork: () => {},
});

export const useNetwork = () => useContext(NetworkContext);

export const NetworkProvider = ({ children }: PropsWithChildren) => {
  const dispatch = useDispatch();
  const { network } = useAppSelector(state => state.app);
  const { data: networks, isLoading: isLoadingNetworks } = useGetNetworksQuery({});

  const [selectedNetwork, setSelectedNetwork] = useState<string | null>(null);
  const [isNetworkInitialized, setIsNetworkInitialized] = useState(false);
  const [networkList, setNetworkList] = useState<NetworkSchemaType[]>([]);

  useEffect(() => {
    if (!isLoadingNetworks && networks) {
      setNetworkList(networks);
    }
  }, [isLoadingNetworks, networks]);

  // Initialize network from localStorage on component mount
  useEffect(() => {
    if (networkList.length === 0) return;

    const localNetwork = localStorage.getItem('network');

    if (localNetwork) {
      // Synchronize Redux with localStorage
      dispatch(setNetwork(localNetwork));
      setSelectedNetwork(localNetwork);
      dispatch(setNetworkInfo(networkList.find(item => item.symbol === localNetwork) ?? null));
    } else if (network) {
      // If no localStorage but Redux has a value, use it
      setSelectedNetwork(network);
      localStorage.setItem('network', network);
      dispatch(setNetworkInfo(networkList.find(item => item.symbol === network) ?? null));
    } else {
      setSelectedNetwork('eth');
      dispatch(setNetwork('eth'));
      localStorage.setItem('network', 'eth');
      dispatch(setNetworkInfo(networkList.find(item => item.symbol === 'eth') ?? null));
    }

    setIsNetworkInitialized(true);
  }, [networkList, dispatch]);

  const updateNetwork = (newNetwork: string) => {
    if (!newNetwork) return;

    setSelectedNetwork(newNetwork);
    dispatch(setNetwork(newNetwork));
    localStorage.setItem('network', newNetwork);
    const selectedNetworkInfo = networkList.find(item => item.symbol === newNetwork);
    dispatch(setNetworkInfo(selectedNetworkInfo ?? null));
  };

  // Keep selectedNetwork in sync with Redux changes
  useEffect(() => {
    if (network && isNetworkInitialized && network !== selectedNetwork) {
      setSelectedNetwork(network);
      localStorage.setItem('network', network);
    }
  }, [network, isNetworkInitialized]);

  const contextValue = useMemo(
    () => ({
      networkList,
      selectedNetwork,
      isNetworkInitialized,
      updateNetwork,
    }),
    [networkList, selectedNetwork, isNetworkInitialized],
  );

  return <NetworkContext.Provider value={contextValue}>{children}</NetworkContext.Provider>;
};
